import 'core-js'
import 'whatwg-fetch'
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch'
// Order matters: AbortController needs fetch which needs Promise (provided by core-js).
import 'md-gum-polyfill'

import Uppy from '@uppy/core'
import Transloadit from '@uppy/transloadit'
import Dashboard from '@uppy/dashboard'

require('@uppy/core/dist/style.min.css')
require('@uppy/dashboard/dist/style.min.css')

// Sidekiq job is perform_in(10s) + CDN copy — reload after that window.
const PUBLISH_RELOAD_MS = 20000

document.addEventListener('alpine:init', () => {
    Alpine.data('admin_episodes_show_x_data', () => ({
        toast: null,
        isReplace: false,

        init() {
            const root = document.getElementById('ql-data-points')
            this.isReplace = root?.dataset.hasManualTranscript === 'true'
            uppy_episode_transcript(this)
        },

        // First upload / replace accepted — Sidekiq still publishing.
        showPublishingToast() {
            this.toast = {
                kind: 'info',
                message: this.isReplace
                    ? 'Replacement transcript received. Publishing to the feed shortly…'
                    : 'Transcript received. It’s being added to the episode and feed shortly…',
            }
            setTimeout(() => window.location.reload(), PUBLISH_RELOAD_MS)
        },

        showErrorToast(message) {
            this.toast = { kind: 'error', message }
        },
    }))
})

function assemblyLandingUrl(assembly) {
    const results = (assembly && assembly.results) || {}
    for (const step of ['export', 'scanned', ':original']) {
        const first = (results[step] || [])[0]
        if (first && (first.ssl_url || first.url)) {
            return first.ssl_url || first.url
        }
    }
    return null
}

function filenameFromLandingUrl(url) {
    if (!url) return null
    try {
        const name = new URL(url).pathname.split('/').filter(Boolean).pop()
        return name && name.toLowerCase().endsWith('.vtt') ? name : null
    } catch (_e) {
        return null
    }
}

function uppy_episode_transcript(alpine) {
    const root = document.getElementById('ql-data-points')
    let signature = root.dataset.signature
    let template_id = root.dataset.template
    let episode_id = root.dataset.episodeId
    let show_id = root.dataset.showId
    let env = root.dataset.env
    let post_url = root.dataset.postUrl
    // Set once per file pick — must match Transloadit fields.filename and the Rails POST.
    let filename = `transcript_upload_${Date.now()}.vtt`

    const syncTransloaditFields = () => {
        const plugin = uppy.getPlugin('Transloadit')
        if (plugin && plugin.opts) {
            plugin.opts.fields = { show_id, episode_id, env, filename }
        }
    }

    const uppy = new Uppy({
        id: 'uppy-episode-transcript',
        debug: true,
        autoProceed: true,
        restrictions: {
            maxNumberOfFiles: 1,
            minNumberOfFiles: 1,
            allowedFileTypes: [".VTT", ".vtt"],
            requiredMetaFields: [],
        },
        locale: {
            strings: {
                youCanOnlyUploadFileTypes: 'You can only upload VTT transcript files',
            },
        },
    })

    uppy.use(Dashboard, {
        trigger: document.getElementById('content-uppy-trigger-transcript'),
        closeAfterFinish: true,
        showProgressDetails: true,
        allowMultipleUploadBatches: false,
        note: 'Please upload an VTT file for the episode transcript',
    })

    uppy.use(Transloadit, {
        service: 'https://api2.transloadit.com',
        params: {
            auth: {
                key: signature,
            },
            template_id: template_id
        },
        waitForMetadata: true,
        // Must wait for export/FTP — otherwise transloadit:complete fires early and cancelAll() kills the assembly.
        waitForEncoding: true,
        importFromUploadURLs: false,
        alwaysRunAssembly: false,
        fields: {
            show_id: show_id,
            episode_id: episode_id,
            env: env,
            filename: filename
        }
    })

    // One new unique name per file; keep TL fields and POST in lockstep (avoid T0/T1 desync).
    uppy.on('file-added', () => {
        filename = `transcript_upload_${Date.now()}.vtt`
        syncTransloaditFields()
    })

    uppy.on('upload-error', (file, error, response) => {
        if (error.isNetworkError) {
            alpine.showErrorToast(
                'Upload failed (network). Check firewall settings and try again. ' + error.message
            )
        }
    })

    uppy.on('transloadit:complete', (assembly) => {
        // When Transloadit cancels/declines an assembly, there will be no export results.
        // Posting Rails with a null `landing_url` will later fail with "Transcription url not available".
        if (assembly?.ok === 'ASSEMBLY_CANCELED') {
            alpine.showErrorToast('Transcript upload was canceled by the file processing pipeline. Please try again.')
            return
        }

        const landingUrl = assemblyLandingUrl(assembly)
        if (!landingUrl) {
            alpine.showErrorToast('Transcript upload completed but the processed VTT URL was missing. Please try again.')
            return
        }
        // Assembly export path / fields are authoritative — plugin filename can desync on file-added.
        const pluginFields = uppy.getPlugin('Transloadit')?.opts?.fields || {}
        const publishFilename =
            assembly?.fields?.filename ||
            filenameFromLandingUrl(landingUrl) ||
            pluginFields.filename ||
            filename

        fetch(post_url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
            },
            body: JSON.stringify({
                show_id: show_id,
                id: episode_id,
                filename: publishFilename,
                landing_url: landingUrl
            })
        }).then(response => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Something went wrong');
            }
        }).then(() => {
            uppy.cancelAll()
            uppy.getPlugin('Dashboard').closeModal()
            alpine.showPublishingToast()
        }).catch(() => {
            alpine.showErrorToast('There was an error uploading the transcript. Please try again.')
        })
    })
}
